import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { useCopyText } from "@/lib/use-copy-text";
import { ListingField } from "./ListingField";

// Die Kennziffer einer Ausschreibung (`Listing.reference`, etwa 2026-0042): die Nummer, über die sich
// Menschen auf eine Ausschreibung beziehen, am Telefon, in einer E-Mail, auf einem Aushang. Überall
// dasselbe Muster, damit man sie überall wiedererkennt: das Wort "Kennziffer", der Wert in
// Festbreitenschrift wie jede andere Kennung zum Abtippen.
//
// - `inline`: "Kennziffer 2026-0042" in einer Zeile, ohne Kopieren. Für Kacheln, Listenzeilen und den
//   Kopf des Dashboards; die Größe kommt von der Zeile, in der sie steht.
// - `field`: eine Angabe wie Frist oder Kategorien, mit Kopieren. Für die Übersicht des Dashboards und
//   den Post, also dort, wo man die Ausschreibung vor sich hat und die Kennziffer weitergibt.
export function ListingReference({
  reference,
  variant = "inline",
}: {
  reference: string;
  variant?: "inline" | "field";
}) {
  const value = <span className="font-mono">{reference}</span>;
  if (variant === "field") {
    return (
      <ListingField label={<FormattedMessage id="share.listings.reference.label" defaultMessage="Kennziffer" />}>
        <span className="inline-flex flex-wrap items-center gap-x-1">
          {value}
          <CopyReference reference={reference} />
        </span>
      </ListingField>
    );
  }
  return (
    <span className="whitespace-nowrap">
      <FormattedMessage
        id="share.listings.reference.inline"
        defaultMessage="Kennziffer {reference}"
        values={{ reference: value }}
      />
    </span>
  );
}

function CopyReference({ reference }: { reference: string }) {
  const intl = useIntl();
  const { copied, copy } = useCopyText("share.listings.copy-reference");
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => void copy(reference)}
      aria-label={intl.formatMessage(
        copied
          ? { id: "share.listings.reference.copied", defaultMessage: "Kennziffer kopiert" }
          : { id: "share.listings.reference.copy", defaultMessage: "Kennziffer kopieren" },
      )}
    >
      {copied ? (
        <FormattedMessage id="design.copied" defaultMessage="Copied" />
      ) : (
        <FormattedMessage id="design.copy" defaultMessage="Copy" />
      )}
    </Button>
  );
}
